import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { BattingRankingStats, DataSection, ResultDataNew, RunnerSafe, ScoringData } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import resultsSliceForPitchType from "../../../classificators/resultsSlice/resultsSliceForPitchType.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import battingScopeContext from "../../../classificators/resultsSlice/battingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import ContextualFilterBase from "../../../filters/contextual/ContextualFilterBase.js";
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import classifiesAsRunBattedIn from '../../../classificators/runnerSafeType/classifiesAsRunBattedIn.js';
import RankingStatCalculatorBase from "../RankingStatCalculatorBase.js";
import ClassifiedAsMultipleOutsFilter from "../../../filters/contextual/ClassifiedAsMultipleOutsFilter.js";
import classifiesAsBalk from "../../../classificators/pitchCall/classifiesAsBalk.js";
import classifiesAsHomeRun from "../../../classificators/plateAppearanceResult/classifiesAsHomeRun.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";

class NewRunsBattedInCalculator extends RankingStatCalculatorBase {
    constructor(
        customResultsProcessor, rankingStatCaseVerifier, numberFormatter, multipleOutsFilter,
        runBattedInClassifier, balkClassifier, homeRunClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(multipleOutsFilter) !== 'undefined' && !(multipleOutsFilter instanceof ContextualFilterBase)) {
            throw new TypeError('multipleOutsFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (typeof(runBattedInClassifier) !== 'undefined' && typeof(runBattedInClassifier) !== 'function') {
            throw new TypeError('runBattedInClassifier must be an function or undefined');
        }
        if (typeof(balkClassifier) !== 'undefined' && typeof(balkClassifier) !== 'function') {
            throw new TypeError('balkClassifier must be an function or undefined');
        }
        if (typeof(homeRunClassifier) !== 'undefined' && typeof(homeRunClassifier) !== 'function') {
            throw new TypeError('homeRunClassifier must be an function or undefined');
        }

        super(
            [ BattingRankingStats.RunsBattedIn ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithStatGrabScoring,
                satisfiesAll(
                    battingScopeContext,
                    resultsSliceForPitchType
                )
            )
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.runBattedInClassifier = runBattedInClassifier || classifiesAsRunBattedIn;
        this.balkClassifier = balkClassifier || classifiesAsBalk;
        this.homeRunClassifier = homeRunClassifier || classifiesAsHomeRun;
        this.multipleOutsFilter = multipleOutsFilter || new ClassifiedAsMultipleOutsFilter();
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        const results = slice.getSmallestSlice();
        const multiOuts = this.multipleOutsFilter.filter(results, session, league);

        let rbis = 0;

        for (let r = 0; r < results.length; r++) {
            const result = results[r];
            
            // @see https://baseball.fandom.com/wiki/Run_batted_in
            // @see https://www.mlb.com/glossary/standard-stats/runs-batted-in
            // There are certain exceptions:, A player does receive an RBI if he is walked or hit by a pitch with the bases loaded. He will also receive an RBI if a runner scores from his sacrifice fly or sacrifice bunt.
            // - a player does not receive an RBI if he hits into a double play and a run scores
            if (multiOuts.some( r => r[ResultDataNew.Id.key] === result[ResultDataNew.Id.key])) {
                continue;
            }

            const paResult = IterableDataProcessor.getObjectContent(
                result, ResultDataNew.getDataPath(ScoringData.PlateAppearanceResult.key, DataSection.Scoring.key)
            );

            // - or if the pitcher balks.
            if (this.balkClassifier(paResult)) {
                continue;
            }

            // When a home run is scored, the batter is also credited with a hit and a run scored, and an RBI for each runner that scores, including himself.
            // @see https://boardgamestips.com/popular/does-a-home-run-count-as-an-rbi/
            const isHomeRun = this.homeRunClassifier(paResult);
            if (isHomeRun) {
                rbis++;
            }

            // get runnerSafe collection
            const runnersSafe = IterableDataProcessor.getObjectContent(
                result, ResultDataNew.getDataPath(ScoringData.RunnerSafe.key, DataSection.Scoring.key)
            );

            rbis += runnersSafe.reduce((sum, rs) => {
                // - if a run is scored on a wild pitch or passed ball or as the result of an error
                if (
                    rs[RunnerSafe.TargetBase.key] === 4
                    && this.runBattedInClassifier(rs[RunnerSafe.Type.key])
                    && (
                        // avoid double home run calculation if app sensds it by mistake
                        !isHomeRun
                        || rs[RunnerSafe.RunnerId.key] !== slice.scope.tagId
                    )
                ) {
                    sum++;
                }

                return sum;
            }, 0);
        }

        // now build stats
        if (rbis > 0) {
            const rbiParam = this.supportedParams[0];
            return RankingStat.fromParameter(
                rbiParam, this.numberFormatter.format(rbis), results.length
            );
        }

        return null;
    }
}

export default NewRunsBattedInCalculator;